import React, { useEffect, useRef } from 'react';
import Swal from 'sweetalert2';

function MinimizerDetector() {
  const isMounted = useRef(true);
  const countdownIntervalRef = useRef(null);
  const swalInstanceRef = useRef(null);
  const isShowingSwalRef = useRef(false);
  const lastValidWidthRef = useRef(window.innerWidth);

  // ============================================================
  // SIMPLIFIED: Only check width
  // ============================================================
  const isWindowSizeValid = (width) => {
    const minWidth = 1024;
    const isMinimized = width < 100;
    return !isMinimized && width >= minWidth;
  };

  // ============================================================
  // Restore function - width only
  // ============================================================
  const restoreWindowSize = () => {
    console.log('🔄 Attempting to restore window size...');
    
    try {
      const elem = document.documentElement;
      if (elem.requestFullscreen && !document.fullscreenElement) {
        elem.requestFullscreen()
          .then(() => {
            console.log('✅ Fullscreen mode activated');
            setTimeout(() => {
              // Only check width
              if (window.innerWidth < 1024) {
                tryResizeWindow();
              }
            }, 500);
          })
          .catch(err => {
            console.log('Fullscreen failed:', err);
            tryResizeWindow();
          });
      } else if (document.fullscreenElement) {
        console.log('Already in fullscreen');
        tryResizeWindow();
      } else {
        tryResizeWindow();
      }
    } catch (error) {
      console.log('Restore failed:', error);
      tryResizeWindow();
    }
  };

  // ============================================================
  // Resize function - width only
  // ============================================================
  const tryResizeWindow = () => {
    try {
      const targetWidth = 1366;
      const targetHeight = 768;
      
      window.moveTo(0, 0);
      window.resizeTo(targetWidth, targetHeight);
      
      console.log(`✅ Window resized to ${targetWidth}x${targetHeight}`);
      
      setTimeout(() => {
        const currentWidth = window.innerWidth;
        
        // Only check width
        if (currentWidth < 1024) {
          console.warn('⚠️ Resize failed or was blocked by browser');
          
          try {
            window.resizeBy(
              targetWidth - window.outerWidth,
              targetHeight - window.outerHeight
            );
            console.log('🔄 Attempted alternative resize method');
          } catch (e) {
            console.log('Alternative resize also failed:', e);
          }
          
          Swal.fire({
            title: '⚠️ Manual Resize Required',
            html: `
              <p>Your browser is blocking automatic window resizing.</p>
              <p>Please manually resize your window to:</p>
              <h2 style="color: #6bcf7f;">1366 × 768</h2>
              <p style="font-size: 12px; color: #999;">or press <strong>F11</strong> for fullscreen</p>
            `,
            icon: 'info',
            confirmButtonText: 'Resize manually'
          });
        } else {
          console.log('✅ Window successfully resized!');
        }
      }, 300);
      
    } catch (e) {
      console.log('Could not resize window:', e);
      Swal.fire({
        title: '⚠️ Manual Resize Required',
        text: 'Please manually resize your window to at least 1024px wide or press F11 for fullscreen.',
        icon: 'info',
        confirmButtonText: 'OK'
      });
    }
  };

  // ============================================================
  // Fullscreen helper
  // ============================================================
  const tryFullscreen = () => {
    const elem = document.documentElement;
    
    if (document.fullscreenElement) {
      console.log('✅ Already in fullscreen');
      return;
    }

    if (elem.requestFullscreen) {
      console.log('📺 Attempting to enter fullscreen...');
      elem.requestFullscreen()
        .then(() => {
          console.log('✅ Fullscreen mode activated');
        })
        .catch(err => {
          console.log('❌ Fullscreen failed:', err.message);
          tryResizeWindow();
        });
    } else {
      console.log('❌ Fullscreen not supported');
      tryResizeWindow();
    }
  };

  // ============================================================
  // Show warning - width only (UPDATED: Cancel goes to fullscreen)
  // ============================================================
  const showWarningMessage = (width) => {
    if (isShowingSwalRef.current) {
      console.log('⚠️ Swal already showing, skipping');
      return;
    }

    // Double-check width before showing
    if (isWindowSizeValid(window.innerWidth)) {
      console.log('✅ Window width is now valid, not showing warning');
      return;
    }

    console.log('📢 Showing window size warning');
    console.log(`📐 Current width: ${width}px`);
    isShowingSwalRef.current = true;

    if (swalInstanceRef.current) {
      try {
        swalInstanceRef.current.close();
      } catch (e) {}
      swalInstanceRef.current = null;
    }

    swalInstanceRef.current = Swal.fire({
      title: '⚠️ Window Width Too Small',
      html: `
        <div style="text-align: center;">
          <p style="font-size: 16px; color: #ff6b6b; font-weight: bold; margin-bottom: 10px;">
            📏 WINDOW TOO NARROW
          </p>
          <p style="font-size: 14px; color: #ccc; margin-bottom: 15px;">
            Your window width is too small for the best experience.<br>
            Please use a wider screen or maximize your window.
          </p>
          <div style="background: rgba(255,255,255,0.05); padding: 12px; border-radius: 8px; margin-bottom: 15px;">
            <p style="font-size: 12px; color: #999; margin: 0;">
              Current Width: <strong style="color: #fff;">${width}px</strong>
            </p>
            <p style="font-size: 12px; color: #999; margin: 4px 0 0 0;">
              Required: <strong style="color: #6bcf7f;">≥ 1024px</strong>
            </p>
          </div>
          <p style="font-size: 14px; color: #ffd93d;">
            Restoring in <strong id="countdown-display" style="font-size: 28px; color: #ff6b6b;">10</strong> seconds
          </p>
          <div style="margin-top: 10px; width: 100%; background: rgba(255,255,255,0.1); height: 4px; border-radius: 2px; overflow: hidden;">
            <div id="countdown-bar" style="width: 100%; height: 100%; background: linear-gradient(90deg, #ff6b6b, #ffd93d, #6bcf7f); transition: width 1s linear;"></div>
          </div>
          <p style="font-size: 11px; color: #666; margin-top: 12px;">
            Click "Restore Now" to resize immediately
          </p>
        </div>
      `,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: '⏪ Restore Now',
      cancelButtonText: '🖥️ Go Fullscreen',
      allowOutsideClick: false,
      allowEscapeKey: false,
      timer: 10000,
      timerProgressBar: true,
      didOpen: () => {
        console.log('✅ Swal dialog opened');
        
        const countdownDisplay = document.getElementById('countdown-display');
        const countdownBar = document.getElementById('countdown-bar');
        
        if (countdownDisplay) {
          countdownDisplay.textContent = '10';
        }

        if (countdownIntervalRef.current) {
          clearInterval(countdownIntervalRef.current);
        }

        let seconds = 10;
        countdownIntervalRef.current = setInterval(() => {
          seconds -= 1;
          
          if (countdownDisplay && isMounted.current) {
            countdownDisplay.textContent = seconds;
          }
          
          if (countdownBar && isMounted.current) {
            const percentage = (seconds / 10) * 100;
            countdownBar.style.width = `${percentage}%`;
          }

          if (seconds <= 0) {
            clearInterval(countdownIntervalRef.current);
            countdownIntervalRef.current = null;
            console.log('⏰ Countdown finished, restoring screen...');
            
            if (swalInstanceRef.current) {
              try {
                swalInstanceRef.current.close();
              } catch (e) {}
              swalInstanceRef.current = null;
            }
            isShowingSwalRef.current = false;
            
            tryFullscreen();
          }
        }, 1000);
      },
      willClose: () => {
        console.log('🔚 Swal dialog closing');
        isShowingSwalRef.current = false;
        if (countdownIntervalRef.current) {
          clearInterval(countdownIntervalRef.current);
          countdownIntervalRef.current = null;
        }
        swalInstanceRef.current = null;
      }
    }).then((result) => {
      console.log('👆 User action:', result);
      isShowingSwalRef.current = false;
      
      if (result.isConfirmed) {
        // User clicked "Restore Now"
        restoreWindowSize();
      } else if (result.dismiss === Swal.DismissReason.cancel) {
        // User clicked "Go Fullscreen" (Cancel button)
        console.log('🖥️ User chose to go fullscreen');
        tryFullscreen();
      } else if (result.dismiss === Swal.DismissReason.timer) {
    // ✅ NEW: Countdown hit zero — go fullscreen automatically
    console.log('⏰ Countdown finished, going fullscreen automatically');
    tryFullscreen();
  }
      
      swalInstanceRef.current = null;
    }).catch((err) => {
      console.log('Swal error:', err);
      isShowingSwalRef.current = false;
      swalInstanceRef.current = null;
    });
  };

  // ============================================================
  // MAIN: useEffect with width-only monitoring
  // ============================================================
  useEffect(() => {
    console.log('🚀 MinimizerDetector MOUNTED');
    console.log('📊 Initial window width:', window.innerWidth);

    // Check initial width
    const initialWidth = window.innerWidth;
    if (!isWindowSizeValid(initialWidth)) {
      console.log('⚠️ Initial window width is too small!');
      setTimeout(() => {
        if (isMounted.current) {
          showWarningMessage(initialWidth);
        }
      }, 1000);
    }

    let resizeTimeout = null;
    
    const handleResize = () => {
      if (resizeTimeout) {
        clearTimeout(resizeTimeout);
      }
      
      resizeTimeout = setTimeout(() => {
        const width = window.innerWidth;
        const isValid = isWindowSizeValid(width);
        const isMinimized = width < 100;
        
        console.log('📐 RESIZE EVENT:', width);
        console.log('📊 Valid width:', isValid);
        console.log('📊 isMinimized:', isMinimized);
        
        // Check if width changed significantly
        const prevWidth = lastValidWidthRef.current;
        const widthChanged = Math.abs(width - prevWidth) > 5;
        
        if (!widthChanged) {
          console.log('⏭️ Skipping: Insignificant width change');
          return;
        }
        
        lastValidWidthRef.current = width;

        // Only check width
        if (!isValid) {
          console.log(`⚠️ Window resized to invalid width: ${width}px`);
          
          if (!isShowingSwalRef.current) {
            showWarningMessage(width);
          }
        } else {
          // Width is valid again - close any open Swal
          console.log('✅ Window width is valid again!');
          if (swalInstanceRef.current) {
            try {
              swalInstanceRef.current.close();
            } catch (e) {}
            swalInstanceRef.current = null;
          }
          isShowingSwalRef.current = false;
          if (countdownIntervalRef.current) {
            clearInterval(countdownIntervalRef.current);
            countdownIntervalRef.current = null;
          }
        }
      }, 300);
    };

    // ============================================================
    // Debug listeners (NO popups)
    // ============================================================
    const handleVisibilityChange = () => {
      if (!isMounted.current) return;

      const isHidden = document.hidden;
      const width = window.innerWidth;
      const isMinimized = width < 100;

      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      console.log('📊 Event: visibilitychange');
      console.log('📊 document.hidden:', isHidden);
      console.log('📊 Window width:', width);
      console.log('📊 isMinimized:', isMinimized);
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');

      if (isHidden && isMinimized) {
        console.log('✅✅✅ WINDOW WAS MINIMIZED! (console only)');
      }
    };

    window.addEventListener('resize', handleResize);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    console.log('✅ All event listeners added!');
    console.log('📌 Minimum required width: 1024px');
    console.log('📌 Current width:', window.innerWidth);
    console.log('📌 ONLY width check will trigger popups');

    return () => {
      console.log('🧹 MinimizerDetector UNMOUNTING');
      isMounted.current = false;
      window.removeEventListener('resize', handleResize);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      
      if (resizeTimeout) {
        clearTimeout(resizeTimeout);
      }
      
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
      if (swalInstanceRef.current) {
        try {
          swalInstanceRef.current.close();
        } catch (e) {}
        swalInstanceRef.current = null;
      }
      isShowingSwalRef.current = false;
      Swal.close();
    };
  }, []);

  return null;
}

export default MinimizerDetector;